import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { AutoDialerSetting, SETTING } from 'interfaces/settings';
import { useAutodialerSettings } from './useAutodialerSettings';

export interface PendingChange {
    setting: SETTING;
    type: 'added' | 'modified' | 'removed';
    oldValue: any;
    newValue: any;
    settingRow: AutoDialerSetting;
}

export interface PendingChangesState {
    totalChanges: number;
    addedSettings: number;
    modifiedSettings: number;
    removedSettings: number;
    changes: PendingChange[];
}

export function usePendingChanges(): PendingChangesState {
    const { savedSettings, adjustedSettings, selectedPlatform, formatSettingValue } = useAutodialerSettings();
    
    return useMemo(() => {
        if (!selectedPlatform) {
            return {
                totalChanges: 0,
                addedSettings: 0,
                modifiedSettings: 0,
                removedSettings: 0,
                changes: [],
            };
        }

        const savedForPlatform = savedSettings.filter(s => s.platform === selectedPlatform);
        const adjustedForPlatform = adjustedSettings.filter(s => s.platform === selectedPlatform);
        
        const changes: PendingChange[] = [];

        // Helper function to create a unique key for each setting row
        const getSettingRowKey = (setting: AutoDialerSetting) => {
            return `${setting.user_id}-${setting.user_group_id}-${setting.team_id}-${setting.locked_user_id}`;
        };

        // Create maps for easier lookup
        const savedMap = new Map<string, AutoDialerSetting>();
        const adjustedMap = new Map<string, AutoDialerSetting>();

        savedForPlatform.forEach(setting => {
            savedMap.set(getSettingRowKey(setting), setting);
        });

        adjustedForPlatform.forEach(setting => {
            adjustedMap.set(getSettingRowKey(setting), setting);
        });

        // Check for new and modified settings
        adjustedForPlatform.forEach(adjustedSetting => {
            const key = getSettingRowKey(adjustedSetting);
            const savedSetting = savedMap.get(key);

            if (!savedSetting) {
                // This is a new setting row - check which settings are set
                Object.values(SETTING).forEach(setting => {
                    const value = adjustedSetting[setting];
                    if (value !== null) {
                        changes.push({
                            setting,
                            type: 'added',
                            oldValue: null,
                            newValue: value,
                            settingRow: adjustedSetting,
                        });
                    }
                });
            } else {
                // Check for modified values in existing setting row
                Object.values(SETTING).forEach(setting => {
                    const oldValue = savedSetting[setting];
                    const newValue = adjustedSetting[setting];
                    
                    if (oldValue !== newValue) {
                        changes.push({
                            setting,
                            type: 'modified',
                            oldValue,
                            newValue,
                            settingRow: adjustedSetting,
                        });
                    }
                });
            }
        });

        // Check for removed settings
        savedForPlatform.forEach(savedSetting => {
            const key = getSettingRowKey(savedSetting);
            const adjustedSetting = adjustedMap.get(key);

            if (!adjustedSetting) {
                // Entire setting row was removed
                Object.values(SETTING).forEach(setting => {
                    const value = savedSetting[setting];
                    if (value !== null) {
                        changes.push({
                            setting,
                            type: 'removed',
                            oldValue: value,
                            newValue: null,
                            settingRow: savedSetting,
                        });
                    }
                });
            }
        });

        const addedSettings = changes.filter(c => c.type === 'added').length;
        const modifiedSettings = changes.filter(c => c.type === 'modified').length;
        const removedSettings = changes.filter(c => c.type === 'removed').length;

        return {
            totalChanges: changes.length,
            addedSettings,
            modifiedSettings,
            removedSettings,
            changes,
        };
    }, [savedSettings, adjustedSettings, selectedPlatform, formatSettingValue]);
} 